<script setup>
import { ref, onMounted, computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useDevices } from '@/composables/useDevices';
import { useToast } from 'primevue/usetoast';
import { useConfirm } from 'primevue/useconfirm';

import InputText from 'primevue/inputtext';
import Dropdown from 'primevue/dropdown';
import Tag from 'primevue/tag';
import Dialog from 'primevue/dialog';
import ConfirmDialog from 'primevue/confirmdialog';
import Tabs from 'primevue/tabs';
import TabList from 'primevue/tablist';
import Tab from 'primevue/tab';
import TabPanels from 'primevue/tabpanels';
import TabPanel from 'primevue/tabpanel';
import Steps from 'primevue/steps';

const route = useRoute();
const router = useRouter();
const toast = useToast();
const confirm = useConfirm();
const deviceApi = useDevices();

const device = ref(null);
const pins = ref([]);
const sensorTypes = ref([]);
const isLoading = ref(true);
const isSaving = ref(false);

// Edit mode
const isEditing = ref(false);
const editForm = ref({});

// Pin configuration dialog
const showPinDialog = ref(false);
const selectedPin = ref(null);
const pinForm = ref({
  sensor_type: '',
  label: '',
  poll_interval: 60,
});

// Create sensor dialog
const showCreateSensorDialog = ref(false);
const sensorForm = ref({
  name: '',
  location: '',
});

// Flash firmware wizard
const showFlashDialog = ref(false);
const flashStep = ref(0);
const flashForm = ref({
  board: '',
  port: '',
  wifiSsid: '',
  wifiPass: '',
  apiKey: '',
  firmwareUrl: '',
});

const flashSteps = ref([
  { label: 'Select Board' },
  { label: 'Connection' },
  { label: 'WiFi & Keys' },
  { label: 'Flash' },
]);

onMounted(async () => {
  await Promise.all([
    fetchDevice(),
    fetchSensorTypes(),
  ]);
});

const fetchDevice = async () => {
  isLoading.value = true;
  try {
    const id = route.params.id;
    device.value = await deviceApi.getDevice(id);
    const responsePins = await deviceApi.getDevicePins(id);
    const pinList = Array.isArray(responsePins) ? responsePins : (Array.isArray(responsePins?.pins) ? responsePins.pins : []);
    pins.value = pinList.map(normalizePin);
  } catch (err) {
    toast.add({ severity: 'error', summary: 'Error', detail: 'Failed to load device', life: 3000 });
    router.push({ name: 'Device List' });
  } finally {
    isLoading.value = false;
  }
};

const fetchSensorTypes = async () => {
  try {
    sensorTypes.value = await deviceApi.getSensorTypes();
  } catch {
    // Silent fail
  }
};

const startEditing = () => {
  editForm.value = {
    name: device.value.name,
    location: device.value.location || '',
  };
  isEditing.value = true;
};

const cancelEditing = () => {
  isEditing.value = false;
  editForm.value = {};
};

const saveDevice = async () => {
  isSaving.value = true;
  try {
    await deviceApi.updateDevice(device.value.id, editForm.value);
    device.value = { ...device.value, ...editForm.value };
    isEditing.value = false;
    toast.add({ severity: 'success', summary: 'Success', detail: 'Device updated', life: 3000 });
  } catch (err) {
    toast.add({ severity: 'error', summary: 'Error', detail: err.message || 'Failed to update device', life: 3000 });
  } finally {
    isSaving.value = false;
  }
};

const openPinDialog = (pin) => {
  selectedPin.value = pin;
  pinForm.value = {
    sensor_type: pin.sensor_type || '',
    label: pin.label || '',
    poll_interval: pin.poll_interval || 60,
  };
  showPinDialog.value = true;
};

const savePin = async () => {
  isSaving.value = true;
  try {
    await deviceApi.configurePin(device.value.id, selectedPin.value.id, pinForm.value);
    await fetchDevice();
    showPinDialog.value = false;
    toast.add({ severity: 'success', summary: 'Success', detail: 'Pin configured', life: 3000 });
  } catch (err) {
    toast.add({ severity: 'error', summary: 'Error', detail: err.message || 'Failed to configure pin', life: 3000 });
  } finally {
    isSaving.value = false;
  }
};

const openCreateSensorDialog = (pin) => {
  selectedPin.value = pin;
  sensorForm.value = {
    name: `${device.value.name} - ${pin.label || pin.pin_number}`,
    location: device.value.location || '',
  };
  showCreateSensorDialog.value = true;
};

const createSensor = async () => {
  isSaving.value = true;
  try {
    const result = await deviceApi.createSensorFromPin(
      device.value.id,
      selectedPin.value.id,
      sensorForm.value
    );
    await fetchDevice();
    showCreateSensorDialog.value = false;
    toast.add({ 
      severity: 'success', 
      summary: 'Sensor Created', 
      detail: `Sensor "${result.name || sensorForm.value.name}" created successfully`,
      life: 3000 
    });
  } catch (err) {
    toast.add({ severity: 'error', summary: 'Error', detail: err.message || 'Failed to create sensor', life: 3000 });
  } finally {
    isSaving.value = false;
  }
};

const sendCommand = async (command) => {
  const commandNames = {
    config: 'Push Configuration',
    reboot: 'Reboot Device',
    identify: 'Identify Device',
  };

  try {
    if (command === 'config') {
      await deviceApi.sendConfigCommand(device.value.id);
    } else if (command === 'reboot') {
      await deviceApi.rebootDevice(device.value.id);
    } else if (command === 'identify') {
      await deviceApi.identifyDevice(device.value.id);
    }
    toast.add({ severity: 'success', summary: 'Command Sent', detail: `${commandNames[command]} command sent`, life: 3000 });
  } catch (err) {
    toast.add({ severity: 'error', summary: 'Error', detail: err.message || 'Failed to send command', life: 3000 });
  }
};

const confirmDelete = () => {
  confirm.require({
    message: `Are you sure you want to delete "${device.value.name}"? This will also remove all associated sensors.`,
    header: 'Delete Device',
    icon: 'fas fa-exclamation-triangle',
    acceptClass: 'p-button-danger',
    accept: async () => {
      try {
        await deviceApi.deleteDevice(device.value.id);
        toast.add({ severity: 'success', summary: 'Deleted', detail: 'Device deleted successfully', life: 3000 });
        router.push({ name: 'Device List' });
      } catch (err) {
        toast.add({ severity: 'error', summary: 'Error', detail: 'Failed to delete device', life: 3000 });
      }
    },
  });
};

const getStatusSeverity = (status) => {
  const map = { online: 'success', offline: 'danger', pending: 'warning', provisioning: 'info' };
  return map[status] || 'secondary';
};

const getPinTypeLabel = (type) => {
  const labels = { gpio: 'GPIO', adc: 'ADC', i2c: 'I2C', spi: 'SPI', uart: 'UART' };
  return labels[type] || type?.toUpperCase();
};

const configuredPins = computed(() => (Array.isArray(pins.value) ? pins.value : []).filter(p => p.sensor_type));
const availablePins = computed(() => (Array.isArray(pins.value) ? pins.value : []).filter(p => !p.sensor_type));

const formatDate = (dateStr) => {
  if (!dateStr) return 'Never';
  return new Date(dateStr).toLocaleString();
};

const normalizePin = (pin) => {
  if (!pin) return pin;
  const pinNumber = pin.pin_number ?? pin.pin ?? pin.number ?? pin.pinNumber ?? '';
  const id = pin.id ?? pin.pin_id ?? pin.pinId ?? pinNumber;
  return {
    ...pin,
    id,
    pin_number: pinNumber,
    label: pin.label ?? pin.name ?? '',
    poll_interval: pin.poll_interval ?? pin.pollInterval ?? 60,
    sensor_type: pin.sensor_type ?? pin.sensorType ?? '',
    sensor_type_label: pin.sensor_type_label ?? pin.sensorTypeLabel ?? pin.sensor_type ?? pin.sensorType,
    type: pin.type ?? pin.pin_type ?? pin.pinType,
    description: pin.description ?? pin.notes ?? '',
  };
};

const getPinDisplay = (pin) => pin?.pin_number ?? pin?.pin ?? '';
</script>

<template>
  <div class="py-4 container-fluid">
    <ConfirmDialog />

    <!-- Loading -->
    <div v-if="isLoading" class="loading-state">
      <i class="fas fa-circle-notch fa-spin"></i>
      <span>Loading device...</span>
    </div>

    <div v-else-if="device" class="device-detail">
      <!-- Back Button -->
      <button class="back-btn" @click="router.push({ name: 'Device List' })">
        <i class="fas fa-arrow-left"></i>
        Back to Devices
      </button>

      <!-- Device Header -->
      <div class="device-header-card">
        <div class="header-main">
          <div class="device-icon" :class="device.status">
            <i class="fas fa-microchip"></i>
          </div>
          
          <div class="device-info" v-if="!isEditing">
            <div class="device-title">
              <h1>{{ device.name }}</h1>
              <Tag :value="device.status" :severity="getStatusSeverity(device.status)" />
            </div>
            <p class="device-type">{{ device.device_type_display }}</p>
            <p v-if="device.location" class="device-location">
              <i class="fas fa-map-marker-alt"></i>
              {{ device.location }}
            </p>
          </div>

          <div class="device-edit-form" v-else>
            <div class="form-row">
              <InputText v-model="editForm.name" placeholder="Device name" />
              <InputText v-model="editForm.location" placeholder="Location" />
            </div>
          </div>
        </div>

        <div class="header-actions">
          <template v-if="!isEditing">
            <button class="action-btn" @click="startEditing">
              <i class="fas fa-pen"></i>
              Edit
            </button>
            <button class="action-btn danger" @click="confirmDelete">
              <i class="fas fa-trash"></i>
              Delete
            </button>
          </template>
          <template v-else>
            <button class="action-btn" @click="cancelEditing">
              <i class="fas fa-times"></i>
              Cancel
            </button>
            <button class="action-btn primary" @click="saveDevice" :disabled="isSaving">
              <i v-if="isSaving" class="fas fa-circle-notch fa-spin"></i>
              <i v-else class="fas fa-check"></i>
              Save
            </button>
          </template>
        </div>
      </div>

      <!-- Quick Stats -->
      <div class="stats-row">
        <div class="stat-card">
          <div class="stat-icon">
            <i class="fas fa-plug"></i>
          </div>
          <div class="stat-content">
            <span class="stat-value">{{ configuredPins.length }}</span>
            <span class="stat-label">Configured Pins</span>
          </div>
        </div>
        <div class="stat-card">
          <div class="stat-icon sensors">
            <i class="fas fa-satellite-dish"></i>
          </div>
          <div class="stat-content">
            <span class="stat-value">{{ device.sensors_count || 0 }}</span>
            <span class="stat-label">Active Sensors</span>
          </div>
        </div>
        <div class="stat-card">
          <div class="stat-icon time">
            <i class="fas fa-clock"></i>
          </div>
          <div class="stat-content">
            <span class="stat-value">{{ device.last_seen ? 'Online' : 'Never' }}</span>
            <span class="stat-label">Last Seen</span>
          </div>
        </div>
      </div>

      <!-- Tabs -->
      <Tabs class="device-tabs" value="pin-config">
        <TabList>
          <Tab value="pin-config">Pin Configuration</Tab>
          <Tab value="commands">Commands</Tab>
          <Tab value="device-info">Device Info</Tab>
        </TabList>
        <TabPanels>
          <TabPanel value="pin-config">
            <div class="pins-section">
              <!-- Configured Pins -->
              <div v-if="configuredPins.length" class="pin-group">
                <h3><i class="fas fa-check-circle"></i> Configured Pins</h3>
                <div class="pins-grid">
                  <div v-for="pin in configuredPins" :key="pin.id" class="pin-card configured">
                    <div class="pin-header">
                      <div class="pin-badge">{{ pin.pin_number }}</div>
                      <Tag :value="getPinTypeLabel(pin.type)" severity="info" />
                    </div>
                    <div class="pin-content">
                      <h4>{{ pin.label || `Pin ${pin.pin_number}` }}</h4>
                      <p class="sensor-type">
                        <i class="fas fa-microchip"></i>
                        {{ pin.sensor_type_label || pin.sensor_type }}
                      </p>
                      <p class="poll-interval">
                        <i class="fas fa-sync"></i>
                        Every {{ pin.poll_interval }}s
                      </p>
                    </div>
                    <div class="pin-actions">
                      <button @click="openPinDialog(pin)" title="Edit">
                        <i class="fas fa-pen"></i>
                      </button>
                      <button v-if="!pin.sensor_id" @click="openCreateSensorDialog(pin)" title="Create Sensor" class="primary">
                        <i class="fas fa-plus"></i>
                      </button>
                      <span v-else class="sensor-linked">
                        <i class="fas fa-link"></i> Sensor linked
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Available Pins -->
              <div v-if="availablePins.length" class="pin-group">
                <h3><i class="fas fa-circle"></i> Available Pins</h3>
                <div class="pins-grid">
                  <div v-for="pin in availablePins" :key="pin.id" class="pin-card available">
                    <div class="pin-header">
                      <div class="pin-badge">{{ getPinDisplay(pin) }}</div>
                      <Tag :value="getPinTypeLabel(pin.type)" />
                    </div>
                    <div class="pin-content">
                      <h4>Pin {{ getPinDisplay(pin) }}</h4>
                      <p class="pin-description">{{ pin.description || 'Not configured' }}</p>
                    </div>
                    <div class="pin-actions">
                      <button @click="openPinDialog(pin)" class="configure-btn">
                        <i class="fas fa-cog"></i>
                        Configure
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <div v-if="!pins.length" class="empty-pins">
                <i class="fas fa-plug"></i>
                <p>No pins available for this device type</p>
              </div>
            </div>
          </TabPanel>

          <!-- Device Commands Tab -->
          <TabPanel value="commands">
            <div class="commands-section">
              <div class="command-card primary" @click="showFlashDialog = true">
                <div class="command-icon flash">
                  <i class="fas fa-bolt"></i>
                </div>
                <div class="command-info">
                  <h4>Flash / Provision</h4>
                  <p>Guided flow to flash firmware and provision WiFi/API keys</p>
                </div>
                <i class="fas fa-chevron-right"></i>
              </div>
              <div class="command-card" @click="sendCommand('config')">
                <div class="command-icon">
                  <i class="fas fa-upload"></i>
                </div>
                <div class="command-info">
                  <h4>Push Configuration</h4>
                  <p>Send current pin configuration to the device via MQTT</p>
                </div>
                <i class="fas fa-chevron-right"></i>
              </div>

              <div class="command-card" @click="sendCommand('identify')">
                <div class="command-icon identify">
                  <i class="fas fa-lightbulb"></i>
                </div>
                <div class="command-info">
                  <h4>Identify Device</h4>
                  <p>Flash LED or beep to locate the physical device</p>
                </div>
                <i class="fas fa-chevron-right"></i>
              </div>

              <div class="command-card warning" @click="sendCommand('reboot')">
                <div class="command-icon reboot">
                  <i class="fas fa-power-off"></i>
                </div>
                <div class="command-info">
                  <h4>Reboot Device</h4>
                  <p>Restart the device remotely</p>
                </div>
                <i class="fas fa-chevron-right"></i>
              </div>
            </div>
          </TabPanel>

          <!-- Device Info Tab -->
          <TabPanel value="device-info">
            <div class="info-section">
              <div class="info-grid">
                <div class="info-item">
                  <label>Hardware ID</label>
                  <code>{{ device.hardware_id }}</code>
                </div>
                <div class="info-item">
                  <label>MQTT Client ID</label>
                  <code>{{ device.mqtt_client_id }}</code>
                </div>
                <div class="info-item">
                  <label>Device Type</label>
                  <span>{{ device.device_type_label || device.device_type }}</span>
                </div>
                <div class="info-item">
                  <label>Firmware Version</label>
                  <span>{{ device.firmware_version || 'Unknown' }}</span>
                </div>
                <div class="info-item">
                  <label>Last Seen</label>
                  <span>{{ formatDate(device.last_seen) }}</span>
                </div>
                <div class="info-item">
                  <label>Created</label>
                  <span>{{ formatDate(device.created_at) }}</span>
                </div>
              </div>
            </div>
          </TabPanel>
        </TabPanels>
      </Tabs>
    </div>

    <!-- Pin Configuration Dialog -->
    <Dialog 
      v-model:visible="showPinDialog" 
      :header="`Configure Pin ${selectedPin?.pin_number}`"
      :style="{ width: '450px' }"
      modal
      class="device-dialog"
    >
      <div class="pin-form">
        <div class="form-group">
          <label>Sensor Type</label>
          <Dropdown 
            v-model="pinForm.sensor_type"
            :options="sensorTypes"
            optionLabel="label"
            optionValue="value"
            placeholder="Select sensor type"
            class="w-full"
          />
        </div>
        
        <div class="form-group">
          <label>Label</label>
          <InputText v-model="pinForm.label" placeholder="e.g., Temperature Sensor" />
        </div>

        <div class="form-group">
          <label>Poll Interval (seconds)</label>
          <InputText v-model.number="pinForm.poll_interval" type="number" min="1" />
        </div>

        <div class="form-actions">
          <button class="btn-secondary" @click="showPinDialog = false">Cancel</button>
          <button class="btn-primary" @click="savePin" :disabled="isSaving">
            <i v-if="isSaving" class="fas fa-circle-notch fa-spin"></i>
            <i v-else class="fas fa-check"></i>
            Save Configuration
          </button>
        </div>
      </div>
    </Dialog>

    <!-- Create Sensor Dialog -->
    <Dialog 
      v-model:visible="showCreateSensorDialog" 
      header="Create Sensor from Pin"
      :style="{ width: '450px' }"
      modal
      class="device-dialog"
    >
      <div class="sensor-form">
        <div class="sensor-preview">
          <div class="preview-icon">
            <i class="fas fa-satellite-dish"></i>
          </div>
          <p>Create a new sensor from <strong>{{ selectedPin?.label || `Pin ${selectedPin?.pin_number}` }}</strong></p>
        </div>

        <div class="form-group">
          <label>Sensor Name</label>
          <InputText v-model="sensorForm.name" placeholder="Living Room Temperature" />
        </div>

        <div class="form-group">
          <label>Location</label>
          <InputText v-model="sensorForm.location" placeholder="Living Room" />
        </div>

        <div class="form-actions">
          <button class="btn-secondary" @click="showCreateSensorDialog = false">Cancel</button>
          <button class="btn-primary" @click="createSensor" :disabled="isSaving">
            <i v-if="isSaving" class="fas fa-circle-notch fa-spin"></i>
            <i v-else class="fas fa-plus"></i>
            Create Sensor
          </button>
        </div>
      </div>
    </Dialog>

    <!-- Flash / Provision Dialog -->
    <Dialog
      v-model:visible="showFlashDialog"
      header="Flash & Provision Device"
      :style="{ width: '640px' }"
      modal
      class="device-dialog flash-dialog"
    >
      <Steps :model="flashSteps" :activeStep="flashStep" class="mb-4" />

      <div v-if="flashStep === 0" class="flash-step">
        <h4>Select your board</h4>
        <p class="muted">Choose the hardware you are flashing.</p>
        <div class="board-grid">
          <button
            class="board-card"
            :class="{ active: flashForm.board === 'esp32' }"
            @click="flashForm.board = 'esp32'"
          >
            <i class="fas fa-microchip"></i>
            <span>ESP32</span>
          </button>
          <button
            class="board-card"
            :class="{ active: flashForm.board === 'esp8266' }"
            @click="flashForm.board = 'esp8266'"
          >
            <i class="fas fa-microchip"></i>
            <span>ESP8266</span>
          </button>
          <button
            class="board-card"
            :class="{ active: flashForm.board === 'rpi' }"
            @click="flashForm.board = 'rpi'"
          >
            <i class="fas fa-server"></i>
            <span>Raspberry Pi (image)</span>
          </button>
        </div>
      </div>

      <div v-else-if="flashStep === 1" class="flash-step">
        <h4>Connect your device</h4>
        <p class="muted">Plug in over USB and select the port.</p>
        <div class="form-group">
          <label>Serial Port</label>
          <input class="flash-input" v-model="flashForm.port" placeholder="e.g., /dev/tty.usbmodem01 or COM3" />
          <small>If unsure, click detect. (UI only; add detection later.)</small>
        </div>
        <button class="btn-secondary" @click="flashForm.port = flashForm.port || 'auto'">
          <i class="fas fa-search"></i> Detect Port
        </button>
      </div>

      <div v-else-if="flashStep === 2" class="flash-step">
        <h4>Network & Credentials</h4>
        <p class="muted">We’ll bake WiFi and API keys into the firmware.</p>
        <div class="form-row">
          <div class="form-group">
            <label>WiFi SSID</label>
            <input class="flash-input" v-model="flashForm.wifiSsid" placeholder="MyHomeWiFi" />
          </div>
          <div class="form-group">
            <label>WiFi Password</label>
            <input class="flash-input" type="password" v-model="flashForm.wifiPass" placeholder="••••••••" />
          </div>
        </div>
        <div class="form-row">
          <div class="form-group">
            <label>API Key</label>
            <input class="flash-input" v-model="flashForm.apiKey" :placeholder="device?.api_key || 'Paste from provision dialog'" />
            <small>You can copy this from the provision credentials modal.</small>
          </div>
          <div class="form-group">
            <label>Firmware URL</label>
            <input class="flash-input" v-model="flashForm.firmwareUrl" placeholder="https://cdn.example.com/firmware.bin" />
          </div>
        </div>
      </div>

      <div v-else class="flash-step">
        <h4>Ready to flash</h4>
        <p class="muted">This will stream the firmware to the device and configure WiFi/API keys.</p>
        <ul class="checklist">
          <li :class="{ done: !!flashForm.board }"><i class="fas fa-check"></i> Board selected</li>
          <li :class="{ done: !!flashForm.port }"><i class="fas fa-check"></i> Port set</li>
          <li :class="{ done: !!flashForm.wifiSsid && !!flashForm.apiKey }"><i class="fas fa-check"></i> WiFi & API key provided</li>
        </ul>
        <button class="btn-primary w-100" :disabled="!flashForm.board || !flashForm.port || !flashForm.wifiSsid || !flashForm.apiKey">
          <i class="fas fa-bolt"></i>
          Flash Firmware (UI only)
        </button>
        <p class="muted text-center mt-3">
          This is a UX flow only. Wire to your flashing backend (e.g., WebSerial + esptool) when ready.
        </p>
      </div>

      <div class="flash-actions">
        <button class="btn-secondary" @click="showFlashDialog = false">Close</button>
        <div class="spacer"></div>
        <button class="btn-secondary" :disabled="flashStep === 0" @click="flashStep--">Back</button>
        <button class="btn-primary" :disabled="flashStep === flashSteps.length - 1" @click="flashStep++">Next</button>
      </div>
    </Dialog>
  </div>
</template>

<style scoped>
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem;
  gap: 1rem;
  color: var(--calid-text-secondary, #94a3b8);
}

.loading-state i {
  font-size: 2rem;
  color: #00d4ff;
}

.device-detail {
  max-width: 1200px;
  margin: 0 auto;
}

.back-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: transparent;
  border: none;
  color: var(--calid-text-secondary, #94a3b8);
  font-size: 0.9rem;
  cursor: pointer;
  margin-bottom: 1.5rem;
  transition: color 0.2s ease;
}

.back-btn:hover {
  color: #00d4ff;
}

.device-header-card {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 1.5rem;
  background: var(--calid-card-bg, rgba(17, 24, 39, 0.8));
  border: 1px solid var(--calid-border, rgba(255, 255, 255, 0.08));
  border-radius: 16px;
  margin-bottom: 1.5rem;
}

.header-main {
  display: flex;
  gap: 1.25rem;
}

.device-icon {
  width: 64px;
  height: 64px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--calid-bg-tertiary, rgba(255, 255, 255, 0.05));
  flex-shrink: 0;
}

.device-icon.online {
  background: rgba(16, 185, 129, 0.15);
  color: #34d399;
}

.device-icon.offline {
  background: rgba(239, 68, 68, 0.15);
  color: #f87171;
}

.device-icon.pending {
  background: rgba(245, 158, 11, 0.15);
  color: #fbbf24;
}

.device-icon i {
  font-size: 1.75rem;
}

.device-title {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 0.25rem;
}

.device-title h1 {
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--calid-text-primary, #f0f0f5);
  margin: 0;
}

.device-type {
  font-size: 0.8rem;
  color: #00d4ff;
  margin: 0 0 0.5rem;
  //text-transform: uppercase;
  letter-spacing: 0.5px;
}

.device-location {
  font-size: 0.9rem;
  color: var(--calid-text-secondary, #94a3b8);
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.375rem;
}

.device-edit-form .form-row {
  display: flex;
  gap: 1rem;
}

.device-edit-form :deep(.p-inputtext) {
  padding: 0.75rem 1rem;
  background: var(--calid-bg-tertiary, rgba(255, 255, 255, 0.03));
  border: 1px solid var(--calid-border, rgba(255, 255, 255, 0.1));
  border-radius: 10px;
  color: var(--calid-text-primary, #f0f0f5);
}

.header-actions {
  display: flex;
  gap: 0.5rem;
}

.action-btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.625rem 1rem;
  background: var(--calid-bg-tertiary, rgba(255, 255, 255, 0.05));
  border: 1px solid var(--calid-border, rgba(255, 255, 255, 0.1));
  border-radius: 10px;
  color: var(--calid-text-secondary, #94a3b8);
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.2s ease;
}

.action-btn:hover {
  background: rgba(255, 255, 255, 0.1);
  color: var(--calid-text-primary, #f0f0f5);
}

.action-btn.primary {
  background: #00d4ff;
  border-color: #00d4ff;
  color: #0a0a0f;
}

.action-btn.primary:hover {
  background: #00b8e0;
}

.action-btn.danger:hover {
  background: rgba(239, 68, 68, 0.15);
  border-color: rgba(239, 68, 68, 0.3);
  color: #f87171;
}

/* Stats Row */
.stats-row {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.stat-card {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1.25rem;
  background: var(--calid-card-bg, rgba(17, 24, 39, 0.8));
  border: 1px solid var(--calid-border, rgba(255, 255, 255, 0.08));
  border-radius: 12px;
}

.stat-icon {
  width: 48px;
  height: 48px;
  background: rgba(0, 212, 255, 0.15);
  color: #00d4ff;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.25rem;
}

.stat-icon.sensors {
  background: rgba(16, 185, 129, 0.15);
  color: #34d399;
}

.stat-icon.time {
  background: rgba(139, 92, 246, 0.15);
  color: #a78bfa;
}

.stat-content {
  display: flex;
  flex-direction: column;
}

.stat-value {
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--calid-text-primary, #f0f0f5);
}

.stat-label {
  font-size: 0.85rem;
  color: var(--calid-text-secondary, #94a3b8);
}

/* Tabs */
:deep(.device-tabs .p-tabpanels) {
  background: transparent;
  padding: 0;
}

:deep(.device-tabs .p-tablist) {
  background: transparent;
  border: none;
  margin-bottom: 1rem;
}

:deep(.device-tabs .p-tab) {
  background: transparent;
  color: var(--calid-text-secondary, #94a3b8);
  padding: 1rem 1.5rem;
}

:deep(.device-tabs .p-tab.p-highlight) {
  color: #00d4ff;
  border-bottom: 2px solid #00d4ff;
}

/* Pins Section */
.pins-section {
  background: var(--calid-card-bg, rgba(17, 24, 39, 0.8));
  border: 1px solid var(--calid-border, rgba(255, 255, 255, 0.08));
  border-radius: 16px;
  padding: 1.5rem;
}

.pin-group {
  margin-bottom: 2rem;
}

.pin-group:last-child {
  margin-bottom: 0;
}

.pin-group h3 {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 1rem;
  font-weight: 600;
  color: var(--calid-text-primary, #f0f0f5);
  margin: 0 0 1rem;
}

.pin-group h3 i {
  color: #00d4ff;
}

.pins-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 1rem;
}

.pin-card {
  background: var(--calid-bg-tertiary, rgba(255, 255, 255, 0.03));
  border: 1px solid var(--calid-border, rgba(255, 255, 255, 0.06));
  border-radius: 12px;
  padding: 1rem;
  transition: all 0.2s ease;
}

.pin-card.configured {
  border-color: rgba(0, 212, 255, 0.2);
}

.pin-card:hover {
  border-color: rgba(0, 212, 255, 0.3);
}

.pin-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.75rem;
}

.pin-badge {
  width: 32px;
  height: 32px;
  background: rgba(0, 212, 255, 0.15);
  color: #00d4ff;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.85rem;
  font-weight: 600;
}

.pin-content h4 {
  font-size: 1rem;
  font-weight: 600;
  color: var(--calid-text-primary, #f0f0f5);
  margin: 0 0 0.25rem;
}

.sensor-type,
.poll-interval,
.pin-description {
  font-size: 0.85rem;
  color: var(--calid-text-secondary, #94a3b8);
  margin: 0.25rem 0;
  display: flex;
  align-items: center;
  gap: 0.375rem;
}

.sensor-type i,
.poll-interval i {
  font-size: 0.75rem;
  color: #00d4ff;
}

.pin-actions {
  display: flex;
  gap: 0.5rem;
  margin-top: 1rem;
  padding-top: 0.75rem;
  border-top: 1px solid var(--calid-border, rgba(255, 255, 255, 0.06));
}

.pin-actions button {
  padding: 0.5rem 0.75rem;
  background: var(--calid-bg-tertiary, rgba(255, 255, 255, 0.05));
  border: 1px solid var(--calid-border, rgba(255, 255, 255, 0.1));
  border-radius: 8px;
  color: var(--calid-text-secondary, #94a3b8);
  font-size: 0.85rem;
  cursor: pointer;
  transition: all 0.2s ease;
}

.pin-actions button:hover {
  background: rgba(0, 212, 255, 0.15);
  color: #00d4ff;
}

.pin-actions button.primary {
  background: rgba(16, 185, 129, 0.15);
  border-color: rgba(16, 185, 129, 0.3);
  color: #34d399;
}

.pin-actions button.primary:hover {
  background: rgba(16, 185, 129, 0.25);
}

.configure-btn {
  display: flex;
  align-items: center;
  gap: 0.375rem;
}

.sensor-linked {
  font-size: 0.8rem;
  color: #34d399;
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.empty-pins {
  text-align: center;
  padding: 2rem;
  color: var(--calid-text-secondary, #94a3b8);
}

.empty-pins i {
  font-size: 2rem;
  margin-bottom: 0.75rem;
  opacity: 0.5;
}

/* Commands Section */
.commands-section {
  background: var(--calid-card-bg, rgba(17, 24, 39, 0.8));
  border: 1px solid var(--calid-border, rgba(255, 255, 255, 0.08));
  border-radius: 16px;
  padding: 1rem;
}

.command-card {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1.25rem;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.command-card:hover {
  background: var(--calid-bg-tertiary, rgba(255, 255, 255, 0.03));
}

.command-card.primary {
  border: 1px solid rgba(0, 212, 255, 0.2);
  background: rgba(0, 212, 255, 0.05);
}

.command-card.warning:hover {
  background: rgba(245, 158, 11, 0.1);
}

.command-icon {
  width: 48px;
  height: 48px;
  background: rgba(0, 212, 255, 0.15);
  color: #00d4ff;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.25rem;
}

.command-icon.identify {
  background: rgba(245, 158, 11, 0.15);
  color: #fbbf24;
}

.command-icon.reboot {
  background: rgba(239, 68, 68, 0.15);
  color: #f87171;
}

.command-icon.flash {
  color: #f59e0b;
  background: rgba(245, 158, 11, 0.15);
}

.command-info {
  flex: 1;
}

.command-info h4 {
  font-size: 1rem;
  font-weight: 600;
  color: var(--calid-text-primary, #f0f0f5);
  margin: 0 0 0.25rem;
}

.command-info p {
  font-size: 0.85rem;
  color: var(--calid-text-secondary, #94a3b8);
  margin: 0;
}

.command-card > i:last-child {
  color: var(--calid-text-tertiary, #64748b);
}

/* Info Section */
.info-section {
  background: var(--calid-card-bg, rgba(17, 24, 39, 0.8));
  border: 1px solid var(--calid-border, rgba(255, 255, 255, 0.08));
  border-radius: 16px;
  padding: 1.5rem;
}

.info-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.5rem;
}

.info-item {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.info-item label {
  font-size: 0.85rem;
  font-weight: 500;
  color: var(--calid-text-secondary, #94a3b8);
}

.info-item span {
  font-size: 0.95rem;
  color: var(--calid-text-primary, #f0f0f5);
}

.info-item code {
  font-family: 'JetBrains Mono', 'SF Mono', monospace;
  font-size: 0.85rem;
  color: #00d4ff;
  background: var(--calid-bg-tertiary, rgba(255, 255, 255, 0.03));
  padding: 0.5rem 0.75rem;
  border-radius: 6px;
  word-break: break-all;
}

/* Dialog Forms */
.pin-form,
.sensor-form {
  padding: 0.5rem 0;
}

.form-group {
  margin-bottom: 1.25rem;
}

.form-group label {
  display: block;
  font-size: 0.9rem;
  font-weight: 500;
  color: var(--calid-text-secondary, #94a3b8);
  margin-bottom: 0.5rem;
}

.form-group :deep(.p-inputtext),
.form-group :deep(.p-dropdown) {
  width: 100%;
  background: var(--calid-bg-tertiary, rgba(255, 255, 255, 0.03));
  border: 1px solid var(--calid-border, rgba(255, 255, 255, 0.1));
  border-radius: 10px;
  color: var(--calid-text-primary, #f0f0f5);
}

.form-group :deep(.p-inputtext) {
  padding: 0.75rem 1rem;
}

.sensor-preview {
  text-align: center;
  padding: 1rem;
  margin-bottom: 1.5rem;
  background: var(--calid-bg-tertiary, rgba(255, 255, 255, 0.03));
  border-radius: 12px;
}

.preview-icon {
  width: 56px;
  height: 56px;
  margin: 0 auto 0.75rem;
  background: rgba(16, 185, 129, 0.15);
  color: #34d399;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
}

.sensor-preview p {
  color: var(--calid-text-secondary, #94a3b8);
  margin: 0;
}

.sensor-preview strong {
  color: var(--calid-text-primary, #f0f0f5);
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  margin-top: 1.5rem;
  padding-top: 1.25rem;
  border-top: 1px solid var(--calid-border, rgba(255, 255, 255, 0.08));
}

.btn-primary,
.btn-secondary {
  padding: 0.75rem 1.25rem;
  border-radius: 10px;
  font-size: 0.95rem;
  font-weight: 500;
  cursor: pointer;
  border: none;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.2s ease;
}

.btn-primary {
  background: #00d4ff;
  color: #0a0a0f;
}

.btn-primary:hover:not(:disabled) {
  background: #00b8e0;
}

.btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-secondary {
  background: var(--calid-bg-tertiary, rgba(255, 255, 255, 0.05));
  border: 1px solid var(--calid-border, rgba(255, 255, 255, 0.1));
  color: var(--calid-text-secondary, #94a3b8);
}

.btn-secondary:hover {
  background: rgba(255, 255, 255, 0.1);
}

/* Dialog overrides */
:deep(.device-dialog .p-dialog) {
  background: var(--calid-card-bg, #111827);
  border: 1px solid var(--calid-border, rgba(255, 255, 255, 0.08));
  border-radius: 20px;
}

:deep(.device-dialog .p-dialog-header) {
  background: transparent;
  border-bottom: 1px solid var(--calid-border, rgba(255, 255, 255, 0.08));
  padding: 1.25rem 1.5rem;
  color: var(--calid-text-primary, #f0f0f5);
}

:deep(.device-dialog .p-dialog-content) {
  background: transparent;
  padding: 1.5rem !important;
}

/* Flash dialog */
.flash-dialog .flash-step h4 {
  margin: 0 0 0.25rem;
}

.flash-dialog .flash-step .muted {
  color: var(--calid-text-secondary, #94a3b8);
  margin-bottom: 1rem;
}

.board-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 0.75rem;
}

.board-card {
  border: 1px solid var(--calid-border, rgba(255, 255, 255, 0.12));
  border-radius: 12px;
  padding: 1rem;
  background: var(--calid-bg-tertiary, rgba(255, 255, 255, 0.03));
  color: var(--calid-text-primary, #f0f0f5);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  transition: all 0.2s ease;
}

.board-card:hover {
  border-color: rgba(0, 212, 255, 0.25);
}

.board-card.active {
  border-color: rgba(0, 212, 255, 0.4);
  box-shadow: 0 0 0 3px rgba(0, 212, 255, 0.1);
}

.board-card i {
  font-size: 1.4rem;
}

.flash-input {
  width: 100%;
  padding: 0.75rem 0.9rem;
  border-radius: 10px;
  border: 1px solid var(--calid-border, rgba(255, 255, 255, 0.12));
  background: var(--calid-bg-tertiary, rgba(255, 255, 255, 0.03));
  color: var(--calid-text-primary, #f0f0f5);
}

.flash-step .form-group {
  margin-bottom: 1rem;
}

.flash-step small {
  color: var(--calid-text-tertiary, #64748b);
}

.flash-step .form-row {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 0.75rem;
}

.checklist {
  list-style: none;
  padding: 0;
  margin: 1rem 0;
  display: grid;
  gap: 0.35rem;
}

.checklist li {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--calid-text-secondary, #94a3b8);
}

.checklist li.done {
  color: #10b981;
}

.flash-actions {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 1.25rem;
}

.flash-actions .spacer {
  flex: 1;
}

/* Light mode */
:root:not(.dark-version) .device-header-card,
:root:not(.dark-version) .stat-card,
:root:not(.dark-version) .pins-section,
:root:not(.dark-version) .commands-section,
:root:not(.dark-version) .info-section {
  background: white;
  border-color: #e5e7eb;
}

:root:not(.dark-version) .device-title h1,
:root:not(.dark-version) .stat-value,
:root:not(.dark-version) .pin-content h4,
:root:not(.dark-version) .command-info h4,
:root:not(.dark-version) .info-item span {
  color: #1f2937;
}
</style>
